import { useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { Swords, Circle, Eye } from 'lucide-react';
import { UserAvatar } from '../game/UserAvatar';
import { isDisplayableRating } from '../../utils/rating';
import { decimale } from '../../utils/formato-data';
import { puntiTorneo, nessunoHaGiocato, POSTO_MUTO } from '../../utils/tournament-helpers';
import type { TournamentStanding } from '../../types/tournament';
import { ScoreSheet, type EsitoPedina } from '../shared/ScoreSheet';
import './TournamentStandings.css';
import '../shared/pedina-podio.css';
import PlayerProfileOverlay from '../profile/PlayerProfileOverlay';
import { isGuestUsername } from '../../utils/guest';

/*
 * L'ordine alfabetico dei pari merito, UGUALE per tutti: `localeCompare` senza
 * lingua segue quella del browser, e due persone vedrebbero le stesse righe in
 * ordine diverso (in turco, per dire, la «i» si ordina altrove). Maiuscole e
 * accenti non contano.
 */
const ORDINE_DEI_NOMI = new Intl.Collator('en', { sensitivity: 'base' });

/** La variazione col segno: «+119», «−55» (il meno tipografico), «0». */
function conSegno(n: number): string {
  if (n > 0) return `+${n}`;
  if (n < 0) return `\u2212${Math.abs(n)}`;
  return '0';
}

interface Props {
  standings: TournamentStanding[];
  /**
   * Il torneo e' a TURNI (svizzero o girone) e non un'arena.
   *
   * Si chiamava `isSwiss`, e il nome era il difetto: un girone e' un formato a
   * turni come lo svizzero — stessi punti, stessi spareggi, nessuna serie — e
   * chiamando la domanda «e' uno svizzero?» ogni chiamante rispondeva `format
   * === 'swiss'` e mandava i gironi nel ramo dell'arena.
   */
  aTurni: boolean;
  /**
   * Il torneo e' un GIRONE, e non uno svizzero.
   *
   * Serve per una cosa sola: il BUCHHOLZ, che nel girone non si mostra. Non
   * perche' valga zero — adesso si calcola anche li', perche' insieme a lui si
   * calcola il Sonneborn-Berger — ma perche' nel girone e' un DOPPIONE DEI
   * PUNTI: affrontando tutti, la somma dei punti degli avversari e' il totale
   * del torneo meno i tuoi, e il Cut 1 toglie solo il piu' debole. Sarebbe una
   * colonna che scende mentre quella accanto sale. E' anche il motivo per cui
   * l'art. 8 FIDE lo vieta come criterio nei gironi: non distingue nessuno.
   *
   * Il Sonneborn-Berger invece resta, nel girone come nello svizzero: li' e' il
   * quarto spareggio, dopo punti, vittorie e scontro diretto, e prima della
   * variazione di rating (`orderRoundRobinStandings`); la scheda «Dettagli» li
   * elenca.
   */
  girone?: boolean;
  /**
   * Il torneo e' finito: gli stati «in gioco» e «pronto» non si disegnano piu'.
   *
   * `isAvailable` non lo abbassa nessuno alla fine — nessuno scrive quel flag
   * dopo l'ultimo turno — quindi la classifica di un torneo concluso mostrava
   * quattro righe che dicevano «Pronto». Pronto a cosa? Uno stato e' una cosa
   * che riguarda ADESSO, e adesso quel torneo non c'e' piu'.
   */
  torneoFinito?: boolean;
  /**
   * Porta al PROPRIO profilo, che e' una schermata (impostazioni, uscita).
   *
   * OPZIONALE, e l'assenza e' una scelta di chi monta: dentro una partita
   * (`ClassificaInPartita`) non va passata, perche' li' un cambio di schermata
   * verrebbe annullato da App — e' la stessa ragione per cui questa lista aveva
   * un bottone che non faceva niente. Senza, anche il proprio nome apre il
   * pannello: sopra la damiera e' l'unica cosa che funziona.
   *
   * Il profilo degli ALTRI non passa di qui: lo apre questo componente.
   */
  onOpenOwnProfile?: (username: string) => void;
  /** Quante righe mostrare. La lobby ne mostra 20 e conta il resto. */
  limit?: number;
  /** L'avatar accanto al nome: la lobby lo mostra, il dettaglio no. */
  showAvatar?: boolean;
  /*
   * Guardare la partita di chi sta giocando, come nell'arena.
   *
   * Assente vuol dire «questa schermata non offre di guardare», e il bottone
   * non si disegna: e' la stessa regola di `occhioConcesso` in
   * ArenaStandings. La guardia vera sta comunque sul server, che rifiuta di
   * far guardare chi sta gia' giocando (`spectator.handler.ts`).
   */
  onSpectate?: (userId: string) => void;
  /**
   * Quante partite fa un turno: 2 in uno svizzero a due manche, 1 altrimenti.
   * Serve a contare i RIPOSI, che valgono una partita per manche.
   */
  manche?: number;
  /**
   * Chi guarda. La sua riga si distingue: con venti righe prima ci si doveva
   * cercare. Assente (ospite, pagina senza utente) = nessuna riga speciale.
   */
  mioUserId?: string | null;
}

/** Lo stato del server → la chiave della sua etichetta. */
const CHIAVE_STATO: Record<NonNullable<TournamentStanding['stato']>, string> = {
  'in-gioco': 'playing',
  assente: 'assente',
  'ha-finito': 'haFinito',
  riposa: 'riposa',
  disconnesso: 'disconnesso',
};

/*
 * La classifica del torneo, per il dettaglio E per la lobby.
 *
 * Era una `<table>` scritta due volte, e a 360px si pagava due volte nello
 * stesso modo: sette colonne non ci stanno, quindi il foglio le AMPUTAVA —
 * `.tournament-tiebreaker-col` e `.tournament-status-col` via sotto i 600px.
 * Ma in uno svizzero Buchholz e Sonneborn-Berger SONO la classifica: sono
 * quello che separa due giocatori a pari punti. Nasconderli sul telefono
 * voleva dire nascondere, alla maggioranza di chi guarda, il motivo per cui
 * uno e' quarto e non secondo. E la tabella era comunque dentro un
 * contenitore a scorrimento orizzontale annidato in una pagina che scorre in
 * verticale: il disegno stesso ammetteva che le colonne non entrassero.
 *
 * Ora e' una lista con la griglia a due righe dell'arena
 * (ArenaStandings.css:1-13, «mandando a capo non si ampute piu' niente»): il
 * rango e il nome sopra, e sotto, dove lo spazio c'e', tutto quello che la
 * tabella buttava — ogni numero con la sua etichetta accanto, che su una riga
 * stretta funziona meglio di un'intestazione di colonna lontana.
 *
 * La riga e' un `<button>` intero. Prima era un `<tr onClick>` con un bottone
 * dentro la cella del nome, e il commento accanto diceva che stava li' solo
 * perche' «dentro una tabella e' l'unico posto dove un bottone puo' stare»:
 * da tastiera si raggiungeva il nome, non la riga. Fuori dalla tabella tutta
 * la riga e' il bersaglio, col dito e col Tab.
 */
export default function TournamentStandings({
  standings,
  aTurni,
  girone = false,
  torneoFinito = false,
  onOpenOwnProfile,
  limit,
  showAvatar = false,
  onSpectate,
  manche = 1,
  mioUserId = null,
}: Props) {
  const { t, i18n } = useTranslation();
  const [profileFor, setProfileFor] = useState<string | null>(null);

  /* «Marco e Giulia», non «Marco, Giulia»: l'elenco dei superati per lo
     scontro diretto con la congiunzione della lingua di chi legge. */
  const elenco = (nomi: string[]): string =>
    /* `Intl.ListFormat` manca su Safari prima della 14.1: li' il costruttore
       lancerebbe, e con lui l'intera classifica del girone. */
    typeof Intl.ListFormat === 'function'
      ? new Intl.ListFormat(i18n?.language || 'en', { type: 'conjunction' }).format(nomi)
      : nomi.join(', ');

  /*
   * Il profilo si apre a PANNELLO, e questo componente se lo monta da se'.
   *
   * Lo stato sta qui e non nei tre posti che lo ospitano — la lobby, il dettaglio
   * del torneo e la classifica dentro una partita — cosi' la porta si apre in
   * tutti e tre senza duplicare niente. E' la stessa scelta, con la stessa
   * ragione, della classifica d'arena.
   *
   * Dentro una partita e' anche l'UNICA che funziona: li' non arrivava nessuna
   * callback, perche' cambiare schermata sopra una damiera viva non si puo', e il
   * bottone del nome era un bottone che non faceva niente.
   */
  const apriProfilo = (username: string) => {
    if (mioUserId && standings.find((p) => p.username === username)?.userId === mioUserId) {
      /* Il proprio profilo e' una schermata — ma solo se chi ci ospita sa portarci:
         in partita la callback non c'e', e allora vale il pannello come per tutti. */
      if (onOpenOwnProfile) {
        onOpenOwnProfile(username);
        return;
      }
    }
    /* Ospiti e bot non hanno un profilo pubblico: il pannello risponderebbe 404.
       `isGuestUsername` prende entrambi, perche' i bot si chiamano `Guest_*`. */
    if (isGuestUsername(username)) return;
    setProfileFor(username);
  };

  if (standings.length === 0) {
    return (
      <div className="tournament-standings-empty">{t('tournament.standings.empty')}</div>
    );
  }

  /*
   * Gli spareggi si mostrano solo da CALCOLATI.
   *
   * Prima dell'avvio valgono zero per tutti, e due etichette con uno zero sotto
   * non sono un'informazione: sono il rumore che si legge proprio quando non
   * significa niente. Lo stesso vale per un torneo chiuso prima che questa
   * classifica imparasse a calcolarli nel girone: niente numeri, niente voci.
   *
   * Si guarda TUTTA la classifica e non le sole righe mostrate: e' una scelta
   * che riguarda la tabella, non la riga, e mostrarla a meta' farebbe righe
   * disuguali.
   *
   * Basta che UNO dei due esista, perche' si calcolano sempre insieme
   * (`recalculateTiebreakers`). E l'uno che serve guardare e' proprio il
   * Buchholz, che nel girone non si disegna: dopo un turno di sole vittorie il
   * Sonneborn-Berger e' ancora zero per tutti — si battono giocatori che di
   * punti non ne hanno ancora — mentre il Buchholz no.
   */
  const spareggiCalcolati = standings.some(
    (p) => (p.buchholz ?? 0) > 0 || (p.sonnebornBerger ?? 0) > 0,
  );

  /*
   * IL POSTO NON SI SCRIVE FINCHE' NESSUNO HA GIOCATO.
   *
   * Stessa ragione della riga qui sopra, su un numero piu' vistoso: prima
   * dell'avvio tutti pareggiano su tutti i criteri e il server li numera tutti
   * primi (un ex aequo vero), quindi venti pre-iscritti leggevano «1» in venti.
   * Vale anche nel primo turno finche' non si chiude una partita: il torneo e'
   * partito, ma il posto e' ancora quello di nessuno. Dal primo risultato — o
   * dal primo RIPOSO, che vale una vittoria — i numeri tornano per tutti
   * insieme. Vedi `nessunoHaGiocato`.
   */
  const postoMuto = nessunoHaGiocato(standings);

  /*
   * A parita' di posto, in ordine alfabetico. Solo per lo schermo: dal 2 ottobre
   * chi e' pari su tutti i criteri (punti, Buchholz, Sonneborn-Berger, vittorie,
   * variazione di rating nel torneo) e' ex aequo, e il server non chiude piu'
   * l'ordine con ora di iscrizione e id: le righe arrivavano come capitava, e
   * prima dell'inizio — tutti a zero — si rimescolavano a ogni rilettura. Il
   * vincitore non passa di qui: lo sceglie il server.
   */
  const ordinate = [...standings].sort(
    (a, b) => a.rank - b.rank || ORDINE_DEI_NOMI.compare(a.username, b.username),
  );
  const mostrate = limit != null ? ordinate.slice(0, limit) : ordinate;
  const restanti = standings.length - mostrate.length;
  /* Vedi il segnaposto piu' sotto: il posto dell'occhio si tiene solo quando in
     questa classifica c'e' almeno una partita aperta. */
  const qualcunoGioca = mostrate.some((p) => p.isPlaying);
  const conOcchio = Boolean(onSpectate && qualcunoGioca);

  /*
   * LE COLONNE, quando c'e' spazio per una tabella.
   *
   * Nei tornei a turni, in un contenitore largo almeno 640px, la riga smette di
   * andare su due righe e diventa una riga di tabella con l'intestazione sopra:
   * # · Giocatore · Punti · V/P/S/R · BH · SB · Δ · Stato. La soglia e' sul
   * CONTENITORE (container query) e non sullo schermo: la stessa classifica sta
   * anche nella colonna laterale della partita, larga ~314px su un monitor
   * grande, e li' deve restare a due righe.
   *
   * Le colonne che non hanno niente da dire non ci sono, e la griglia si
   * costruisce di conseguenza: BH e SB prima che si calcolino (come la riga a
   * due piani, che non li mostra), BH nel girone, lo stato a torneo finito.
   */
  const mostraBH = aTurni && spareggiCalcolati && !girone;
  const mostraSB = aTurni && spareggiCalcolati;
  /*
   * Δ, la variazione di rating nel torneo: l'ultimo spareggio, nello svizzero
   * (2 ottobre 2026) come nel girone (4 ottobre). Senza vederla, due righe pari
   * su tutti gli altri criteri avrebbero posti e medaglie diversi senza una
   * ragione a schermo.
   *
   * Solo quando c'e': nei tornei non valutati e' zero per tutti. Non aspetta BH e SB: si muove a
   * ogni partita, e al primo turno e' l'unico spareggio che c'e'.
   */
  const mostraVariazione = aTurni && standings.some((p) => (p.variazioneRating ?? 0) !== 0);

  /*
   * LO SCORE SHEET: una pedina per partita, in ordine, con una forma a parte
   * per il riposo. E' l'andamento, che V/P/S/R non puo' dire — i contatori non
   * hanno un ordine, e «0/0/5 con due punti» non si spiega da solo.
   *
   * Non basta «ha uno storico»: lo vuole COMPLETO. A cavallo della migration
   * 122 la fila di chi era gia' iscritto e' piu' corta delle partite giocate, e
   * tre pedine accanto a cinque giocate sono una bugia; chi non soddisfa la
   * guardia non rende nessuna fila e tiene i suoi numeri, che ci sono sempre.
   *
   * `aTurni &&` tiene fuori il ramo morto del formato arena: quella strada
   * passa da `getStandings`, che la colonna non la seleziona nemmeno.
   */
  const conFila = (p: TournamentStanding): boolean =>
    aTurni && (p.resultsHistory?.length ?? 0) > 0 && p.resultsHistory!.length === p.gamesPlayed;
  const mostraScoreSheet = standings.some(conFila);
  const mostraStato = aTurni && !torneoFinito;
  /*
   * Oro, argento e bronzo solo a torneo FINITO (4 ottobre 2026). Durante il
   * torneo la classifica e' provvisoria: al primo turno sono tutti a zero, e la
   * pedina d'oro andava a chi decideva l'ultimo spareggio — nel girone il posto
   * in tabella, cioe' il rating di partenza — prima di una sola mossa. Finche'
   * si gioca, il posto e' un numero come gli altri.
   */
  const sulPodio = (rank: number): boolean => torneoFinito && rank <= 3;
  const colonne = [
    '34px', 'minmax(0, 1fr)', '3.5rem',
    '5.5rem',
    ...(mostraBH ? ['3rem'] : []),
    ...(mostraSB ? ['3rem'] : []),
    ...(mostraVariazione ? ['3.5rem'] : []),
    ...(mostraStato ? ['7.5rem'] : []),
  ].join(' ');

  return (
    <div
      className={`tournament-standings-box${aTurni ? ' tournament-standings-box--tabella' : ''}`}
      style={{ '--colonne-classifica': colonne } as CSSProperties}
    >
      {/*
        * La legenda della fila, una riga sopra la lista e una volta sola.
        *
        * Pieno, mezzo e vuoto si leggono da soli; il QUADRATO no, e soprattutto
        * non si legge quanto vale: nello svizzero il riposo vale due punti come
        * una vittoria, nel girone zero. Senza questa frase la somma delle
        * pedine non torna coi punti e sembra un difetto dei conti — il
        * riscontro e' la `R` di V/P/S/R.
        *
        * Niente oro qui: `winPlus` nasce da serie e boost, che sono dell'arena.
        */}
      {mostraScoreSheet && (
        <p className="tournament-standings-hint" data-testid="tournament-standings-sheet-hint">
          {t('tournament.standings.scoreSheet.hint')}
          {' · '}
          {girone
            ? t('tournament.standings.scoreSheet.hintRiposoGirone')
            : t('tournament.standings.scoreSheet.hintRiposoSvizzero')}
        </p>
      )}
      {aTurni && (
        /* L'intestazione si vede solo in tabella (CSS). Per i lettori di schermo
           e' muta: ogni cella porta gia' la sua etichetta, nascosta alla vista
           quando c'e' l'intestazione a dirla. */
        <div className="tournament-standings-head" aria-hidden="true">
          <span className="tournament-standings-head-cells">
            <span>{t('tournament.standings.columns.rank')}</span>
            <span>{t('tournament.standings.columns.player')}</span>
            <span className="tournament-standings-head-num">{t('tournament.standings.columns.points')}</span>
            <span className="tournament-standings-head-num">{t('tournament.standings.columns.winsDrawsLossesByes')}</span>
            {mostraBH && <span className="tournament-standings-head-num">{t('tournament.swiss.buchholz')}</span>}
            {mostraSB && <span className="tournament-standings-head-num">{t('tournament.swiss.sb')}</span>}
            {mostraVariazione && <span className="tournament-standings-head-num" title={t('tournament.swiss.variazioneTitolo')}>{t('tournament.swiss.variazione')}</span>}
            {mostraStato && <span>{t('tournament.standings.columns.status')}</span>}
          </span>
          {conOcchio && <span className="tournament-standing-watch-slot" />}
        </div>
      )}
      <ul className="tournament-standings" data-testid="tournament-standings">
        {mostrate.map((player) => (
          <li
            key={player.userId}
            className="tournament-standing-row"
            data-top={sulPodio(player.rank) ? player.rank : undefined}
            data-mia={mioUserId && player.userId === mioUserId ? '' : undefined}
          >
            <button
              type="button"
              className="tournament-standing-open"
              onClick={() => apriProfilo(player.username)}
            >
              <span className="tournament-standing-mark">
                {/* Il trattino non si fa leggere: un «—» ad alta voce e' rumore,
                    e la riga continua a dire nome, punti e V/P/S/R. */}
                {postoMuto ? (
                  <span className="tournament-standing-rank" aria-hidden="true">{POSTO_MUTO}</span>
                ) : sulPodio(player.rank) ? (
                  /* La stessa pedina dell'arena, col numero dentro: la medaglia
                     disegnata, a questa misura, non si leggeva e non diceva il
                     posto. Il colore segue il POSTO, quindi due secondi pari
                     merito sono due argenti. */
                  <span
                    className="pedina-podio"
                    data-testid="tournament-standings-medal"
                    data-medal={player.rank === 1 ? 'gold' : player.rank === 2 ? 'silver' : 'bronze'}
                  >
                    {player.rank}
                  </span>
                ) : (
                  <span className="tournament-standing-rank">{player.rank}</span>
                )}
              </span>

              <span className="tournament-standing-who">
                {showAvatar && (
                  <UserAvatar username={player.username} avatar={player.avatar} variant="opponent" size="sm" />
                )}
                <span className="tournament-player-name">{player.username}</span>
                {/* Per chi non vede il colore della riga: «(tu)» letto, non mostrato. */}
                {mioUserId && player.userId === mioUserId && (
                  <span className="tournament-standing-me-sr">({t('common.you')})</span>
                )}
                {/*
                  * «SD»: davanti per lo SCONTRO DIRETTO (girone, 11 ottobre 2026).
                  *
                  * Il saldo ordina ma non e' una colonna: senza questo segno due
                  * righe pari su punti e vittorie potevano stare in un ordine che
                  * nessun numero spiegava, con SB e Δ magari a favore di chi sta
                  * dietro. Solo dove ha deciso — il server manda la lista solo
                  * allora — quindi nel caso normale non c'e' niente.
                  *
                  * Sul telefono il `title` non si vede e il tocco apre il
                  * profilo: la sigla si spiega nella legenda degli spareggi
                  * (scheda Dettagli e regole), che nomina «SD».
                  *
                  * Uno `<span>` e non un bottone: tutta la riga e' gia' un
                  * bottone, e un bottone dentro un bottone e' HTML illecito (vedi
                  * l'occhio, piu' sotto). La frase intera la porta il `title` per
                  * il mouse e il testo nascosto per chi ascolta.
                  */}
                {girone && player.scontroDirettoSu && player.scontroDirettoSu.length > 0 && (
                  <span
                    className="tournament-standing-sd"
                    data-testid="classifica-scontro-diretto"
                    title={t('tournament.standings.sdSpiega', { nomi: elenco(player.scontroDirettoSu) })}
                  >
                    <span aria-hidden="true">{t('tournament.standings.sdSigla')}</span>
                    <span className="tournament-standing-me-sr">
                      {t('tournament.standings.sdSpiega', { nomi: elenco(player.scontroDirettoSu) })}
                    </span>
                  </span>
                )}
                {isDisplayableRating(player.rating) && (
                  <span className="tournament-player-rating">({player.rating})</span>
                )}
              </span>

              <span className="tournament-standing-pts">{puntiTorneo(player.points)}</span>

              <span className="tournament-standing-meta">
                {/*
                  * L'ORDINE (11 ottobre 2026): punti, poi V/P/S/R, poi gli
                  * spareggi — BH (solo svizzero), SB, Δ — e in fondo lo stato.
                  * Prima V/P/S/R stava dopo gli spareggi; ma e' il numero che
                  * spiega i punti, e chi legge una classifica lo cerca subito
                  * accanto a loro. In tabella l'ordine del DOM e' l'ordine delle
                  * colonne (`display: contents`); a due piani il foglio mette
                  * V/P/S/R e lo stato sulla seconda riga e gli spareggi sulla
                  * terza. Nell'arena, stessa idea: V/P/S prima della serie.
                  */}
                {/*
                  * Vinte / patte / sconfitte, non piu' «vinte su giocate».
                  *
                  * `V/P 4/6` diceva quante ne aveva vinte su quante giocate, e
                  * lasciava il resto da indovinare: 4 su 6 sono due patte, due
                  * sconfitte, o una e una? La terna le distingue, ed e' come si
                  * legge un tabellone.
                  *
                  * La sigla e' tradotta: sono le iniziali di vittoria, patta e
                  * sconfitta NELLA LINGUA di chi legge — `W/D/L` in inglese,
                  * `S/R/N` in tedesco, `Z/R/P` in polacco. Tradurre i numeri
                  * sarebbe assurdo; non tradurre le lettere lo era altrettanto.
                  *
                  * Il totale delle giocate sparisce da qui: e' la somma dei tre,
                  * e il riposo — che nel totale entrava senza dirlo — non
                  * gonfia piu' un numero che sembrava contare le partite.
                  */}
                {/*
                  * I RIPOSI, come quarto numero: V/P/S/R.
                  *
                  * La terna esclude il riposo di proposito (vedi sopra), ma
                  * senza dirlo i conti non tornavano: 0/0/5 con due punti, e
                  * nessun modo di capire da dove venissero. Stava in una voce a
                  * parte, «Rip», che compariva solo quando c'era; ora e' sempre
                  * il quarto numero, anche a zero, cosi' in tabella la colonna
                  * non ha buchi. Si conta in TURNI: le partite giocate contano
                  * anche i riposi, uno per manche, e con l'andata e ritorno un
                  * turno di riposo vale due «partite». Fuori dai turni (arena)
                  * il riposo non esiste e resta la terna.
                  */}
                {aTurni ? (
                  <span className="tournament-standing-fact tournament-standing-fact--vpsr" data-testid="vpsr">
                    <b>{t('tournament.standings.columns.winsDrawsLossesByes')}</b>
                    {player.gamesWon}/{player.gamesDrawn}/{player.gamesLost}/{Math.round(
                      (player.gamesPlayed - player.gamesWon - player.gamesDrawn - player.gamesLost) / Math.max(1, manche),
                    )}
                  </span>
                ) : (
                  <span className="tournament-standing-fact">
                    <b>{t('tournament.standings.columns.winsDrawsLosses')}</b>{player.gamesWon}/{player.gamesDrawn}/{player.gamesLost}
                  </span>
                )}
                {aTurni && (spareggiCalcolati || mostraVariazione) ? (
                  <>
                    {/* `decimale` e non `toFixed`: quest'ultimo scrive sempre il
                        punto, e in mezza Europa il separatore decimale e' la
                        virgola — e il Sonneborn-Berger finisce a mezzo punto per
                        definizione, perche' le patte valgono meta' dei punti
                        dell'avversario. Vedi `utils/formato-data`. */}
                    {mostraBH && (
                      <span className="tournament-standing-fact">
                        <b>{t('tournament.swiss.buchholz')}</b>{player.buchholz != null ? decimale(player.buchholz) : '-'}
                      </span>
                    )}
                    {mostraSB && (
                      <span className="tournament-standing-fact">
                        <b>{t('tournament.swiss.sb')}</b>{player.sonnebornBerger != null ? decimale(player.sonnebornBerger) : '-'}
                      </span>
                    )}
                    {mostraVariazione && (
                      <span
                        className="tournament-standing-fact"
                        title={t('tournament.swiss.variazioneTitolo')}
                        data-testid="classifica-variazione"
                      >
                        {/* «Δ» da solo un lettore di schermo non lo spiega, e in tabella
                            l'intestazione e' muta: il nome per intero lo legge lui. */}
                        <b aria-hidden="true">{t('tournament.swiss.variazione')}</b>
                        <span className="tournament-standing-me-sr">{t('tournament.swiss.variazioneTitolo')} </span>
                        {conSegno(player.variazioneRating ?? 0)}
                      </span>
                    )}
                  </>
                ) : null}
                {/*
                  * Nei tornei a TURNI lo stato lo dice il server, e «Pronto» non
                  * c'e' piu': veniva da `is_available`, un concetto d'arena che
                  * nello svizzero vale sempre vero, e diceva soltanto «adesso
                  * non gioca» — il 28 settembre anche di chi non si era
                  * presentato. In arena resta com'era.
                  */}
                {torneoFinito ? null : aTurni ? (
                  (() => {
                    const stato = player.stato ?? (player.isPlaying ? 'in-gioco' : null);
                    if (!stato) return null;
                    return (
                      <span className="tournament-standing-state" data-state={stato}>
                        {stato === 'in-gioco' && <Swords size={12} />}
                        {t(`tournament.standings.status.${CHIAVE_STATO[stato]}`)}
                      </span>
                    );
                  })()
                ) : player.isPlaying ? (
                  <span className="tournament-standing-state" data-state="playing">
                    <Swords size={12} /> {t('tournament.standings.status.playing')}
                  </span>
                ) : player.isAvailable ? (
                  <span className="tournament-standing-state" data-state="ready">
                    <Circle size={8} fill="currentColor" /> {t('tournament.standings.status.ready')}
                  </span>
                ) : null}
                {/*
                  * Lo score sheet, ULTIMO di questo piano e larga quanto la
                  * riga (`flex-basis: 100%` nel foglio): si prende una riga
                  * sua, dove ci stanno ~16 pedine a 360px e ~35 in tabella.
                  *
                  * Ultimo nel DOM e non accanto a V/P/S/R, che sembrerebbe piu'
                  * logico: senza spareggi calcolati non c'e' nessuna regola di
                  * `order`, quindi vale l'ordine del DOM, e una fila larga 100%
                  * messa in mezzo spingerebbe lo stato su un piano da solo.
                  *
                  * ⚠️ NON porta la classe `tournament-standing-fact`: la
                  * divisione in piani si accende con
                  * `:has(> .tournament-standing-fact:not(.--vpsr))`, e vestita
                  * da «fatto» questa fila l'accenderebbe al primo turno
                  * aggiungendo una riga vuota a ogni riga di classifica.
                  */}
                {conFila(player) && (
                  <ScoreSheet
                    esiti={player.resultsHistory as EsitoPedina[]}
                    testId="tournament-standings-sheet"
                    className="tournament-standing-sheet"
                  />
                )}
              </span>
            </button>

            {/*
              * L'occhio sta FUORI dal bottone della riga, non dentro.
              *
              * Tutta la riga e' un `<button>` — scelta voluta, spiegata in
              * cima a questo file: da tastiera si raggiunge la riga, non il
              * nome. Un bottone dentro un bottone pero' e' HTML illecito, e
              * i browser lo sciolgono come vogliono: il clic sull'occhio
              * finirebbe a volte sulla riga. Quindi e' un fratello, e il
              * foglio li mette in fila.
              *
              * `isPlaying` e' la condizione vera: senza una partita aperta
              * non c'e' niente da guardare e il server risponderebbe
              * `NOT_PLAYING`. Solo l'icona, perche' a 360px questa riga e'
              * gia' al suo tetto; il nome della cosa vive nell'`aria-label`.
              */}
            {/*
              * IL POSTO DELL'OCCHIO SI TIENE ANCHE QUANDO L'OCCHIO NON C'E'.
              *
              * Il bottone misura `--touch-target-min` (44px) e li toglie alla
              * riga: comparendo su una riga sola faceva slittare a sinistra i
              * punti di QUELLA riga, e la colonna dei punteggi si sfilava.
              * Peggio ancora nel tempo: appariva e spariva a ogni turno, e la
              * classifica ballava sotto gli occhi mentre la si leggeva.
              *
              * Il segnaposto vuoto tiene la stessa larghezza, quindi i punti
              * di tutte le righe restano incolonnati e l'occhio si accende e
              * si spegne senza muovere niente.
              *
              * Ma solo se in questa classifica QUALCUNO sta giocando: fra un
              * turno e l'altro, e su un torneo finito, di occhi non ce ne sara'
              * nessuno e 44px di margine vuoto su ogni riga sarebbero spazio
              * rubato a 360px. Cosi' lo scarto avviene una volta sola, quando
              * il turno si apre, e per tutte le righe insieme.
              */}
            {conOcchio && onSpectate && (
              player.isPlaying ? (
                <button
                  type="button"
                  className="tournament-standing-watch"
                  data-testid="tournament-standings-watch"
                  aria-label={t('spectate.watchPlayer', { username: player.username })}
                  title={t('spectate.watchPlayer', { username: player.username })}
                  onClick={() => onSpectate(player.userId)}
                >
                  <Eye size={16} aria-hidden="true" />
                </button>
              ) : (
                <span className="tournament-standing-watch-slot" aria-hidden="true" />
              )
            )}
          </li>
        ))}
      </ul>

      {profileFor && (
        <PlayerProfileOverlay username={profileFor} onClose={() => { setProfileFor(null); }} />
      )}

      {restanti > 0 && (
        <p className="tournament-more-players">
          {t('tournament.standings.morePlayers', { count: restanti })}
        </p>
      )}
    </div>
  );
}
